import config, { AUTH_ENABLED, AUTH_MODE } from "../config";

const authConfig = config?.auth;
const authEnabled = AUTH_ENABLED;
const isInternalAuth = AUTH_MODE === "internal";
const webUri =
  typeof window !== "undefined"
    ? `${window.location.protocol}//${window.location.host}/`
    : "";
const loginUrl =
  authEnabled && !isInternalAuth && authConfig?.uri && authConfig?.clientId
    ? `${authConfig.uri}/login?client_id=${authConfig.clientId}&response_type=code&redirect_uri=${webUri}`
    : "";

export const LOGIN_REDIRECT_KEY = "auth_redirect_path";

export const isAuthConfigured = () => authEnabled;
export const isInternalAuthMode = () => isInternalAuth;

export function redirectToLogin(message, err) {
  if (!authEnabled) {
    console.debug("Auth not configured; skipping redirect.", message, err);
    return;
  }
  console.debug("Redirect to login:", message, err);

  const redirectPath = `${window.location.pathname}${window.location.search}`;
  if (redirectPath && redirectPath !== "/login") {
    window.localStorage.setItem(LOGIN_REDIRECT_KEY, redirectPath);
  }

  if (isInternalAuth) {
    const loginPath = "/login";
    const currentPath = window.location.pathname.replace(/\/+$/, "") || "/";
    if (currentPath !== loginPath) {
      window.location.href = loginPath;
    }
    return;
  }

  if (!loginUrl) {
    console.error("Auth login URL not configured.");
    return;
  }

  console.debug("Login URL is:", loginUrl);
  window.location.href = loginUrl;
}

export function parseAuthQueryString() {
  if (!authEnabled || isInternalAuth) return null;
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const error = params.get("error");
  if (error) {
    throw new Error(params.get("error_description") || "Invalid configuration");
  }
  return code;
}

export async function handleCode(code) {
  if (!authEnabled || isInternalAuth) return;
  console.debug("Exchanging code for token:", code);

  let data;
  try {
    data = await authRequest("authorization_code", {
      redirect_uri: webUri,
      code: code,
    });
  } catch (err) {
    return redirectToLogin("Couldn't validate code", err);
  }

  store(data);

  const url = new URL(window.location);
  url.searchParams.delete("code");
  window.history.pushState({}, "", url);
}

async function authRequest(grant_type, data) {
  if (!authEnabled || isInternalAuth) {
    throw new Error("Auth flow not configured");
  }
  console.debug("Doing", grant_type, " with", data);
  let body = new URLSearchParams();
  body.append("grant_type", grant_type);
  body.append("client_id", authConfig.clientId);
  Object.keys(data).forEach((key) => {
    body.append(key, data[key]);
  });

  const url = `${authConfig.uri}/oauth2/token`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: body,
  });

  if (!response.ok) {
    console.debug(response);
    throw new Error("Bad response from auth endpoint");
  }

  return response.json();
}

export async function getToken() {
  if (!authEnabled) return null;

  const token = window.localStorage.getItem("access_token");

  if (token === null) {
    return null;
  }

  if (isInternalAuth) {
    return token;
  }

  try {
    const payload = payloadFromToken(token);
    if (Math.floor(Date.now() / 1000) < payload.exp) {
      return token;
    }
  } catch (err) {
    console.debug(err);
  }

  console.debug("Expired token");

  try {
    return refreshToken();
  } catch (err) {
    return redirectToLogin("Error refreshing tokens", err);
  }
}

function decodeBase64Url(value) {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padding = normalized.length % 4;
  const padded = padding ? normalized + "=".repeat(4 - padding) : normalized;
  return window.atob(padded);
}

export function payloadFromToken(token) {
  const parts = token.split(".");

  if (parts.length !== 3) throw new Error("Invalid token");

  return JSON.parse(decodeBase64Url(parts[1]));
}

export async function refreshToken() {
  if (!authEnabled) return null;
  if (isInternalAuth) {
    return window.localStorage.getItem("access_token");
  }
  window.localStorage.removeItem("id_token");
  window.localStorage.removeItem("access_token");
  window.localStorage.removeItem("refresh_token");
  let data = await authRequest("refresh_token", {
    refresh_token: window.localStorage.getItem("refresh_token"),
  });
  console.debug("Tokens refreshed");

  store(data);

  return data.access_token;
}

function store(data) {
  if (data?.id_token) {
    window.localStorage.setItem("id_token", data.id_token);
  }
  if (data?.access_token) {
    window.localStorage.setItem("access_token", data.access_token);
  }
  if (data?.refresh_token) {
    window.localStorage.setItem("refresh_token", data.refresh_token);
  }
  if (data?.user?.username) {
    window.localStorage.setItem("username", data.user.username);
  }
}

export function logOut() {
  if (!authEnabled) return;
  console.log("logout clicked");
  localStorage.removeItem("id_token");
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  redirectToLogin("user logged out");
}
